// ==================== AUTHENTICATION & USER ====================

export type UserRole = 'bank' | 'company';

export interface User {
  id: string;
  username: string;
  role: UserRole;
  bankId?: string; // Se role = 'bank', indica quale banca
  createdAt: Date;
}

export interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  selectedBankId: string | null; // Banca corrente selezionata
}

// ==================== BANK ====================

export interface Bank {
  id: string;
  name: string;
  code: string; // Codice univoco banca
  contactPerson: string;
  createdAt: Date;
  updatedAt: Date;
}

// ==================== CHECKLIST TYPES ====================

export type ChecklistType = 'ibip' | 'protection' | 'motor' | 'pnc' | 'ppi';

export const CHECKLIST_NAMES: Record<ChecklistType, string> = {
  ibip: 'IBIP Saving',
  protection: 'Protection Vita',
  motor: 'Motor',
  pnc: 'P&C Non Motor',
  ppi: 'PPI Standard'
};

// ==================== CHECKLIST RECORD (Single Control) ====================

export interface ChecklistRecord {
  id: string;
  bankId: string;
  checklistType: ChecklistType;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string; // User ID

  // Tab 1: Informazioni Principali
  infoPrincipali: InfoPrincipali;

  // Tab 2: Parte A - Controlli rapporto con Clienti
  parteA: ParteAControlli;

  // Tab 3: Parte B - Controlli AML
  parteB: ParteBControlli;

  // Tab 4: Parte C - Controlli altri aspetti
  parteC: ParteCControlli;

  // Metadata
  status: 'draft' | 'completed' | 'reviewed';
  notes: string;
}

// ==================== TAB 1: INFORMAZIONI PRINCIPALI ====================

export interface InfoPrincipali {
  // Identificativi polizza
  numeroPolizza: string;
  compagnia: 'Compagnia A' | 'Compagnia B' | 'Compagnia C' | 'Compagnia D' | '';
  tipologiaProdotto: string;
  nomeCommerciale: string;

  // Contraente
  codiceFiscaleContraente: string;
  nominativoContraente: string;

  // Operazione
  tipologiaOperazione: TipologiaOperazione;
  dataSottoscrizione: Date | null;
  dataOperazione: Date | null;
  importo: number | null;

  // Controllo
  dataControllo: Date | null;
  controlloEffettuato: 'remoto' | 'filiale' | '';
  referente: string;
}

export type TipologiaOperazione =
  | 'Nuova emissione'
  | 'Versamenti aggiuntivi'
  | 'Riscatti/liquidazioni'
  | 'Variazione contraente'
  | 'Quietanzamento'
  | 'Rimborso premio non goduto'
  | '';

// ==================== TAB 2: PARTE A - Controlli Rapporto con Clienti ====================

export interface ParteAControlli {
  // A1 - Modalità di pagamento dei premi
  a1: {
    dataIncasso: Date | null;
    correttoAddebito: YesNo;
    esito: EsitoControllo;
    note: string;
  };

  // A2 - Consegna MUP
  a2: {
    attestazioneConsegnaMUP: YesNo;
    esito: EsitoControllo;
    note: string;
  };

  // A3 - Consegna informativa precontrattuale
  a3: {
    presenzaPropostaContratto: SiNo;
    presenzaFirmaProposta: YesNo;
    // Campi condizionali per tipo operazione
    presenzaRichiestaVariazioneVers?: SiNo;
    presenzaFirmaRichiestaVers?: YesNo;
    presenzaRichiestaPrestazione?: YesNo;
    presenzaFirmaPrestazione?: YesNo;
    presenzaRichiestaVariazioneCont?: YesNo;
    presenzaFirmaVariazioneCont?: YesNo;
    esito: EsitoControllo;
    note: string;
  };

  // A4 - Rispetto adeguatezza contratti (solo IBIP)
  a4?: {
    presenzaQuestionario: SiNo;
    presenzaFirmaQuestionario: YesNo;
    esito: EsitoControllo;
    note: string;
  };

  // A5 - Materiale pubblicitario
  a5: {
    materialePersonalizzato: 'Autorizzato' | 'Non autorizzato' | 'n/a' | '';
    esito: EsitoControllo;
    note: string;
  };

  // A6 - Siti internet intermediari (solo IBIP e Protection)
  a6?: {
    aggiornamentoSetInformativo: NAStatus;
    presenzaSetSito: NAStatus;
    esito: EsitoControllo;
    note: string;
  };
}

// ==================== TAB 3: PARTE B - Controlli AML ====================

export interface ParteBControlli {
  // B1 - Validità documento identificativo
  b1: {
    documentiIdentificazione: YesNo;
    esito: EsitoControllo;
    note: string;
  };

  // B2 - Documentazione Adeguata Verifica
  b2: {
    qAdv: SiNo;
    presenzaFirmaQAdv: YesNo;
    esito: EsitoControllo;
    note: string;
  };
}

// ==================== TAB 4: PARTE C - Controlli Altri Aspetti ====================

export interface ParteCControlli {
  // C1 - Consensi privacy
  c1: {
    presenzaFirmaPrivacy: YesNo;
    esito: EsitoControllo;
    note: string;
  };

  // C2 - Formazione obbligatoria
  c2: {
    formazioneObbligatoria: YesNo;
    esito: EsitoControllo;
    note: string;
  };

  // C3 - Iscrizione RUI
  c3: {
    collocatoreAbilitato: SiNo;
    verificaIscrizioneRUI: NAStatus;
    esito: EsitoControllo;
    note: string;
  };

  // C4 - Documentazione FATCA (non presente in Motor e P&C)
  c4?: {
    presenzaModuloFATCA: SiNo;
    presenzaFirmaFATCA: SiNo;
    esito: EsitoControllo;
    note: string;
  };
}

// ==================== COMMON TYPES ====================

export type YesNo = 'Si' | 'No' | '';
export type SiNo = 'si' | 'no' | '';
export type EsitoControllo = 'OK' | 'KO' | '';
export type NAStatus = 'Si' | 'No' | 'NA' | '';

// ==================== DASHBOARD DATA ====================

export interface BankDashboard {
  bankId: string;
  bankName: string;
  totalRecords: number;
  recordsByType: Record<ChecklistType, number>;
  recordsByStatus: {
    draft: number;
    completed: number;
    reviewed: number;
  };
  esitiSummary: {
    [key: string]: { // A1, A2, B1, etc.
      totale: number;
      positivi: number;
      negativi: number;
      percentualeOK: number;
    };
  };
  lastUpdate: Date;
}

export interface CompanyDashboard {
  banks: BankDashboard[];
  totalRecordsAllBanks: number;
  aggregatedEsiti: {
    [key: string]: {
      totale: number;
      positivi: number;
      negativi: number;
      percentualeOK: number;
    };
  };
  benchmarkData: {
    bestPerforming: string; // Bank ID
    needsAttention: string[]; // Bank IDs
  };
}

// ==================== CHECKLIST CONFIG ====================

export interface ChecklistConfig {
  type: ChecklistType;
  name: string;
  hasA4: boolean;  // Adeguatezza (solo IBIP)
  hasA6: boolean;  // Siti internet (IBIP e Protection)
  hasC4: boolean;  // FATCA (tutti tranne Motor e P&C)
  allowedOperations: TipologiaOperazione[];
}

export const CHECKLIST_CONFIGS: Record<ChecklistType, ChecklistConfig> = {
  ibip: {
    type: 'ibip',
    name: 'IBIP Saving',
    hasA4: true,
    hasA6: true,
    hasC4: true,
    allowedOperations: ['Nuova emissione', 'Versamenti aggiuntivi', 'Riscatti/liquidazioni', 'Variazione contraente']
  },
  protection: {
    type: 'protection',
    name: 'Protection Vita',
    hasA4: false,
    hasA6: true,
    hasC4: true,
    allowedOperations: ['Nuova emissione', 'Quietanzamento']
  },
  motor: {
    type: 'motor',
    name: 'Motor',
    hasA4: false,
    hasA6: false,
    hasC4: false,
    allowedOperations: ['Nuova emissione', 'Quietanzamento']
  },
  pnc: {
    type: 'pnc',
    name: 'P&C Non Motor',
    hasA4: false,
    hasA6: false,
    hasC4: false,
    allowedOperations: ['Nuova emissione', 'Quietanzamento']
  },
  ppi: {
    type: 'ppi',
    name: 'PPI Standard',
    hasA4: false,
    hasA6: false,
    hasC4: true,
    allowedOperations: ['Nuova emissione', 'Quietanzamento', 'Rimborso premio non goduto']
  }
};

// ==================== FACTORY FUNCTIONS ====================

export function createEmptyInfoPrincipali(): InfoPrincipali {
  return {
    numeroPolizza: '',
    compagnia: '',
    tipologiaProdotto: '',
    nomeCommerciale: '',
    codiceFiscaleContraente: '',
    nominativoContraente: '',
    tipologiaOperazione: '',
    dataSottoscrizione: null,
    dataOperazione: null,
    importo: null,
    dataControllo: null,
    controlloEffettuato: '',
    referente: ''
  };
}

export function createEmptyParteA(checklistType: ChecklistType): ParteAControlli {
  const config = CHECKLIST_CONFIGS[checklistType];

  const parteA: ParteAControlli = {
    a1: { dataIncasso: null, correttoAddebito: '', esito: '', note: '' },
    a2: { attestazioneConsegnaMUP: '', esito: '', note: '' },
    a3: { presenzaPropostaContratto: '', presenzaFirmaProposta: '', esito: '', note: '' },
    a5: { materialePersonalizzato: '', esito: '', note: '' }
  };

  if (config.hasA4) {
    parteA.a4 = { presenzaQuestionario: '', presenzaFirmaQuestionario: '', esito: '', note: '' };
  }

  if (config.hasA6) {
    parteA.a6 = { aggiornamentoSetInformativo: '', presenzaSetSito: '', esito: '', note: '' };
  }

  return parteA;
}

export function createEmptyParteB(): ParteBControlli {
  return {
    b1: { documentiIdentificazione: '', esito: '', note: '' },
    b2: { qAdv: '', presenzaFirmaQAdv: '', esito: '', note: '' }
  };
}

export function createEmptyParteC(checklistType: ChecklistType): ParteCControlli {
  const config = CHECKLIST_CONFIGS[checklistType];

  const parteC: ParteCControlli = {
    c1: { presenzaFirmaPrivacy: '', esito: '', note: '' },
    c2: { formazioneObbligatoria: '', esito: '', note: '' },
    c3: { collocatoreAbilitato: '', verificaIscrizioneRUI: '', esito: '', note: '' }
  };

  if (config.hasC4) {
    parteC.c4 = { presenzaModuloFATCA: '', presenzaFirmaFATCA: '', esito: '', note: '' };
  }

  return parteC;
}

export function createEmptyChecklistRecord(
  bankId: string,
  checklistType: ChecklistType,
  userId: string
): ChecklistRecord {
  return {
    id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    bankId,
    checklistType,
    createdAt: new Date(),
    updatedAt: new Date(),
    createdBy: userId,
    infoPrincipali: createEmptyInfoPrincipali(),
    parteA: createEmptyParteA(checklistType),
    parteB: createEmptyParteB(),
    parteC: createEmptyParteC(checklistType),
    status: 'draft',
    notes: ''
  };
}
